import { AuthBrandLogo } from '../../features/auth/AuthBrandLogo';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../features/auth/useUser';
import { createOrganization, declineMyInvitation } from '../../features/organizations/api';
import './CreateOrganizationPage.css';

/** 組織作成を塞いでいる招待 (409 PENDING_INVITATION_EXISTS のレスポンス) */
type BlockingInvitation = {
  id: string;
  organization_id: string;
  organization_name: string;
  expires_at: string;
};

export default function CreateOrganizationPage() {
  const navigate = useNavigate();
  const { user } = useUser();

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    display_name: '',
    description: '',
    billing_email: user?.email || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [warning, setWarning] = useState<string>('');
  // 409 で返ってきた「組織作成を塞いでいる招待」。全件辞退するまで作成できないため
  // 1件ずつ辞退できるよう配列で保持する。
  const [blockingInvitations, setBlockingInvitations] = useState<BlockingInvitation[]>([]);
  const [decliningId, setDecliningId] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // slugフィールドの自動生成
    if (name === 'name' && !formData.slug) {
      const generatedSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData((prev) => ({
        ...prev,
        slug: generatedSlug,
      }));
    }
  };

  const handleDecline = async (invitationId: string) => {
    setDecliningId(invitationId);
    setError('');
    try {
      await declineMyInvitation(invitationId);
      const remaining = blockingInvitations.filter((inv) => inv.id !== invitationId);
      setBlockingInvitations(remaining);
      if (remaining.length === 0) {
        setWarning('招待を辞退しました。組織を作成できます。');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '招待の辞退に失敗しました');
    } finally {
      setDecliningId('');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setWarning('');
    setBlockingInvitations([]);
    setIsSubmitting(true);

    try {
      // 必須フィールドのチェック
      if (!formData.name.trim()) {
        setError('組織名は必須です');
        return;
      }
      if (!formData.slug.trim()) {
        setError('スラッグは必須です');
        return;
      }

      // slugの形式チェック
      const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
      if (!slugRegex.test(formData.slug)) {
        setError('スラッグは小文字の英数字とハイフンのみ使用できます');
        return;
      }

      const response = await createOrganization({
        name: formData.name,
        slug: formData.slug,
        display_name: formData.display_name || formData.name,
        description: formData.description || undefined,
        billing_email: formData.billing_email || undefined,
      });

      // 警告メッセージがある場合は表示（エラーではない）
      if (response.warning) {
        setWarning(response.warning);
      }

      // 成功後、完了ページに遷移（トークンと組織名を渡す）
      navigate('/organizations/created', {
        replace: true,
        state: {
          token: response.token,
          organizationName: formData.display_name || formData.name,
        },
      });
    } catch (err: any) {
      console.error('Failed to create organization:', err);

      // 招待にブロックされている場合は、全件を辞退導線付きで提示する
      // (1件だけ辞退しても残りが塞ぐため、全件見えていないと脱出できない)。
      if (err.response?.data?.code === 'PENDING_INVITATION_EXISTS') {
        setBlockingInvitations(err.response.data.invitations || []);
      }

      // サーバーが返す error 文言を最優先で表示する（409 の PENDING_INVITATION_EXISTS 等、
      // 理由がユーザーの次の行動を決めるため）。無い場合のみステータス別の既定文言にフォールバック。
      const serverError = err.response?.data?.error;
      if (serverError) {
        setError(serverError);
      } else if (err.response?.status === 401) {
        setError('認証が必要です。ログインしてください。');
      } else if (err.response?.status === 403) {
        setError('組織を作成する権限がありません。');
      } else {
        setError('組織の作成に失敗しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <AuthBrandLogo />
        <div className="auth-card">
          <div className="auth-header">
            <h1 className="auth-title">新しい組織を作成</h1>
            {user?.email && (
              <div className="user-email-badge">{user.email}</div>
            )}
          </div>

          <div className="auth-content">
            {/* エラーメッセージ */}
            {error && (
              <div className="auth-message auth-message-error">
                {error}
              </div>
            )}

            {/* 招待にブロックされている場合の脱出路。
                全件辞退するまで組織を作成できないため、残っている招待を全て並べる。 */}
            {blockingInvitations.length > 0 && (
              <div className="blocking-invitations">
                <p className="blocking-invitations-hint">
                  参加しない場合は辞退すると、組織を作成できるようになります。
                </p>
                <ul className="blocking-invitations-list">
                  {blockingInvitations.map((inv) => (
                    <li key={inv.id} className="blocking-invitation-item">
                      <span className="blocking-invitation-org">{inv.organization_name}</span>
                      <button
                        type="button"
                        className="blocking-invitation-decline"
                        onClick={() => handleDecline(inv.id)}
                        disabled={decliningId === inv.id}
                      >
                        {decliningId === inv.id ? '辞退中...' : '辞退する'}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 警告メッセージ（同一ドメインの既存組織） */}
            {warning && (
              <div className="auth-message auth-message-warning">
                <strong>ご注意:</strong> {warning}
              </div>
            )}

            <form onSubmit={handleSubmit} className="form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  組織名 <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="例: 株式会社ライトメディア"
                  required
                  maxLength={100}
                  disabled={isSubmitting}
                />
                <p className="form-help">
                  組織の正式名称を入力してください
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="slug" className="form-label">
                  スラッグ <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  className="form-input"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="例: lightmedia"
                  required
                  pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                  maxLength={50}
                  disabled={isSubmitting}
                />
                <p className="form-help">
                  URLに使用される一意の識別子（小文字の英数字とハイフンのみ）
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="display_name" className="form-label">
                  表示名
                </label>
                <input
                  type="text"
                  id="display_name"
                  name="display_name"
                  className="form-input"
                  value={formData.display_name}
                  onChange={handleInputChange}
                  placeholder="例: Light Media"
                  maxLength={100}
                  disabled={isSubmitting}
                />
                <p className="form-help">
                  画面に表示される短い名前（省略可、未入力の場合は組織名が使用されます）
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  説明
                </label>
                <textarea
                  id="description"
                  name="description"
                  className="form-textarea"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="組織の説明を入力してください"
                  rows={4}
                  maxLength={500}
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="billing_email" className="form-label">
                  請求先メールアドレス
                </label>
                <input
                  type="email"
                  id="billing_email"
                  name="billing_email"
                  className="form-input"
                  value={formData.billing_email}
                  onChange={handleInputChange}
                  placeholder="請求通知の送信先"
                  maxLength={255}
                  disabled={isSubmitting}
                />
                <p className="form-help">
                  請求関連の通知を受け取るメールアドレス（省略可、未入力の場合はログインメールアドレスが使用されます）
                </p>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/onboarding')}
                  disabled={isSubmitting}
                >
                  戻る
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '作成中...' : '組織を作成'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
